import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Aperture, Download, ShieldCheck } from "lucide-react";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Motion";
import { EventCard } from "@/components/EventCard";

const chapters = [
  ["01", "Arrive before the noise", "We study the light, the machines and the rhythm of the paddock before the first engine turns."],
  ["02", "Wait for velocity", "Every frame is selected for tension: a knee near asphalt, a reflection crossing carbon, a crowd reduced to blur."],
  ["03", "Deliver the original", "Protected previews stay public. The purchased files arrive clean, detailed and ready for large-format use."],
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.18]);

  useEffect(() => {
    api.get("/events").then((response) => setEvents(response.data)).catch(() => setEvents([]));
  }, []);

  const featured = events.filter((event) => event.featured);
  const hero = featured.find((event) => event.hero) || featured[0] || events[0];

  return (
    <div data-testid="home-page" className="bg-onyx">
      <Seo title="Cinematic Event Photography" description="Browse cinematic motorcycle and automotive event photography, preview protected albums, and purchase high-resolution digital downloads." />
      <section ref={heroRef} className="spotlight relative flex min-h-screen items-end overflow-hidden pt-28">
        {hero?.cover_url && (
          <motion.div className="absolute inset-0" style={{ y: imageY, scale: imageScale }}>
            <img src={hero.cover_url} alt="Motorcycle event photography at night" className="h-full w-full object-cover opacity-58" />
          </motion.div>
        )}
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,7,8,0.96),rgba(7,7,8,0.5)_48%,rgba(7,7,8,0.18)),linear-gradient(0deg,#070708_0%,transparent_40%)]" />
        <div className="relative z-10 mx-auto grid w-full max-w-[1600px] gap-10 px-4 pb-20 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-8">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6 font-mono text-xs uppercase tracking-[0.45em] text-gold">mellowjmedia / event archive</motion.p>
            <h1 className="font-display text-5xl font-light leading-[0.98] tracking-[-0.04em] text-white sm:text-7xl lg:text-[7.5rem]">
              <MaskedLine delay={0.18}>Machines in</MaskedLine>
              <MaskedLine delay={0.34}><span className="italic text-titanium">their most</span></MaskedLine>
              <MaskedLine delay={0.5}><span className="text-gold">honest light.</span></MaskedLine>
            </h1>
          </div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82, duration: 0.8 }} className="flex flex-col justify-end lg:col-span-4">
            <p className="max-w-md text-base leading-8 text-zinc-300">A darkroom for track nights, supercar gatherings and custom builds. Browse every event freely, inspect watermarked frames, then collect the clean high-resolution originals.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="hero-explore-button" to="/events" className="group inline-flex items-center gap-3 bg-gold px-7 py-4 font-mono text-xs uppercase tracking-[0.26em] text-onyx transition-colors hover:bg-titanium">
                Explore event library <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link data-testid="hero-license-button" to="/licensing" className="inline-flex items-center gap-3 border border-white/20 px-7 py-4 font-mono text-xs uppercase tracking-[0.26em] text-white transition-colors hover:border-gold hover:text-gold">
                License work
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 border-y border-gold/20 py-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <span><strong data-testid="hero-events-count" className="block font-display text-3xl normal-case tracking-normal text-white">{events.length || 3}</strong>Events</span>
              <span><strong className="block font-display text-3xl normal-case tracking-normal text-white">{events.reduce((sum, event) => sum + (event.photo_count || 0), 0) || 11}</strong>Frames</span>
              <span><strong className="block font-display text-3xl normal-case tracking-normal text-white">7d</strong>Delivery</span>
            </div>
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-gold lg:block">
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </section>

      <div className="overflow-hidden border-y border-gold/15 bg-zinc-950 py-5">
        <div className="marquee-track flex w-max gap-10 font-mono text-xs uppercase tracking-[0.42em] text-gold/55">
          {[...Array(2)].map((_, copy) => <span key={copy} className="whitespace-nowrap">Track nights · Supercar rallies · Custom builds · Watermarked previews · High-resolution originals · Instant digital delivery ·</span>)}
        </div>
      </div>

      <section className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
        <Reveal className="mb-14 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-gold">Manifesto</p>
            <h2 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">Photography with a pulse.</h2>
          </div>
          <div className="grid gap-px bg-white/10 md:grid-cols-3 lg:col-span-8">
            {chapters.map(([number, title, body]) => (
              <div key={number} data-testid={`manifesto-chapter-${number}`} className="bg-onyx p-7">
                <span className="font-display text-5xl italic text-gold/35">{number}</span>
                <h3 className="mt-8 font-lux text-lg uppercase tracking-[0.16em] text-white">{title}</h3>
                <p className="mt-4 text-sm leading-7 text-zinc-500">{body}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <div className="grid gap-6 lg:grid-cols-12">
          {featured.slice(0, 2).map((event, index) => <div key={event.id} className={index === 0 ? "lg:col-span-7" : "lg:col-span-5"}><EventCard event={event} index={index} large={index === 0} /></div>)}
        </div>
      </section>

      <section className="border-t border-white/10 bg-carbon/60 px-4 py-24 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-[1600px]">
          <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.4em] text-gold">Recent albums</p>
              <h2 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">Fresh from the darkroom.</h2>
            </div>
            <Link data-testid="recent-albums-view-all-button" to="/events" className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.26em] text-titanium hover:text-gold">View all events <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {events.slice(0, 3).map((event, index) => <EventCard key={event.id} event={event} index={index} />)}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1600px] gap-px px-4 py-24 sm:px-8 md:grid-cols-3 lg:px-12 lg:py-32">
        {[[Aperture, "Protected previews", "Every public frame carries a restrained watermark and technical metadata."], [ShieldCheck, "Secure Stripe checkout", "Pay as a guest or sign in before checkout for a permanent download history."], [Download, "Original delivery", "Receive high-resolution single files or complete event albums as a zip archive."]].map(([Icon, title, body], index) => (
          <Reveal key={title} delay={index * 0.1} className="border border-white/10 bg-onyx p-8">
            <Icon className="h-6 w-6 text-gold" />
            <h3 className="mt-8 font-lux text-lg uppercase tracking-[0.18em] text-white">{title}</h3>
            <p className="mt-4 text-sm leading-7 text-zinc-500">{body}</p>
          </Reveal>
        ))}
      </section>
    </div>
  );
}
